import * as React from "react";

import Form from "@ant-design/compatible/es/form";
import { t } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import { Typography, InputNumber } from "antd";

import {
    BaseFormField,
    ChangeError,
    EditFormProps,
    EditProps,
    EditState,
    UNLOCKED,
    ValueLock,
} from "components/form/legacyFields";
import { BaseFormDisplay } from "components/form/legacyFields/formDisplay";
import * as CT from "org/risks/content/types";
import {
    CustomizedValidation,
    FormComponentProps,
    FormCreate,
    FormRootComponentProps,
    ValidationRule,
    validationRulesConverter,
} from "utils/form";

import { mapForm, simpleMappers, WrappedFormUtils } from "../../../utils/form";
import { formatNumber } from "../content/utils";
import * as U from "../content/utils";

import * as T from "./types";

const { Text } = Typography;

interface CreateNumericMetadataFieldProps<ID extends string, R>
    extends FormComponentProps<ID, string, R>, NumericInputCommonProps {
    id: ID;
    validators?: ValidationRule<string>[];
    empty?: boolean;
    validationError?: CustomizedValidation;
}

interface CreateNumericMetadataFieldState {
    editing: boolean;
}

class CreateNumericInput<ID extends string, R> extends React.PureComponent<
    CreateNumericMetadataFieldProps<ID, R>,
    CreateNumericMetadataFieldState
> {
    private innerField = React.createRef<HTMLInputElement>();

    state: CreateNumericMetadataFieldState = {
        editing: false,
    };

    render = () => {
        const {
            form: { isFieldValidating, getFieldError },
            id,
            label,
            validationError,
        } = this.props;
        const { editing } = this.state;
        const readonly = !(
            editing ||
            isFieldValidating(id) ||
            getFieldError(id)
        );

        return (
            <BaseFormField
                getComponent={this.getComponent}
                getReadonlyComponent={this.getReadonlyComponent}
                onClick={this.onLabelClick}
                hasFeedback={false}
                readonly={readonly}
                label={label}
                validationError={validationError}
            />
        );
    };

    getComponent = () => {
        const { form, id, validators, label } = this.props;
        const { getFieldDecorator } = form;
        return getFieldDecorator(id, {
            rules: validationRulesConverter<string>()(
                id,
                form,
                validators,
                label
            ),
            valuePropName: "defaultValue",
            trigger: "onBlur",
            validateFirst: true,
            preserve: true,
        })(
            <InputNumber
                ref={this.innerField}
                size="large"
                onPressEnter={this.blur}
                onBlur={this.onBlur}
                onFocus={this.onFocus}
            />
        );
    };

    getReadonlyComponent = () => {
        const {
            form: { getFieldValue },
            id,
        } = this.props;
        const value = getFieldValue(id);
        const displayed = Number(value);
        const empty = value === undefined || value === "" || isNaN(displayed);
        return (
            <Text type={empty ? "secondary" : undefined}>
                {empty ? t("Form_No_Field_Set") : formatNumber(displayed)}
            </Text>
        );
    };

    onLabelClick = () => {
        if (!this.state.editing) {
            this.setState({ editing: true }, this.giveFocus);
        }
    };

    onFocus = () => {
        if (!this.state.editing) {
            this.setState({ editing: true });
        }
    };

    onBlur = () => {
        if (this.state.editing) {
            this.setState({ editing: false });
        }
    };

    blur = () => {
        if (this.innerField.current) {
            this.innerField.current.blur();
        }
    };

    giveFocus = () => {
        if (this.innerField.current) {
            this.innerField.current.focus();
        }
    };
}

type NumericEditProps = EditProps<number | undefined, string>;

const EditNumericInputForm = FormCreate<
    { [K: string]: string },
    NumericEditProps &
        EditFormProps<string> &
        FormRootComponentProps<{ [K: string]: string }>
>({
    onFieldsChange(props, fields) {
        const field = fields[props.id];
        if (!field) {
            return;
        }

        if (!field.validating && !field.errors) {
            const value = parseNumber(field.value);
            const promise = props.onChange(value);
            if (promise) {
                const { setPendingChanges, setChangeErrors } = props;
                setPendingChanges(true);
                promise
                    .then(() => {
                        setPendingChanges(false);
                    })
                    .catch((err) => {
                        setChangeErrors([
                            { message: err.toString(), field: props.id },
                        ]);
                    });
            } else {
                const { unlockValue } = props;
                unlockValue();
            }
        } else {
            const { lockValue } = props;
            lockValue(field.value, field.validating, field.errors);
        }
    },
    mapPropsToFields(props) {
        const lock: ValueLock<string> = props.lock;
        const value: string | number | undefined =
            lock.type === "Locked" ? lock.value : props.value;
        return {
            [props.id]: Form.createFormField({
                value,
                errors: props.changeErrors,
                validating: props.pendingChanges || undefined,
            }),
        };
    },
})(CreateNumericInput);

EditNumericInputForm.displayName = "EditNumericInputForm";

class EditNumericInput extends React.PureComponent<
    NumericEditProps,
    EditState<string>
> {
    mounted = false;
    state: EditState<string> = {
        pendingChanges: false,
        changeErrors: undefined,
        lock: UNLOCKED,
    };

    render = () => {
        return (
            <EditNumericInputForm
                {...this.props}
                {...this.state}
                setPendingChanges={this.setPendingChanges}
                setChangeErrors={this.setChangeErrors}
                lockValue={this.lockValue}
                unlockValue={this.unlockValue}
            />
        );
    };

    setPendingChanges = (pendingChanges: boolean) => {
        if (!this.mounted) {
            return;
        }
        this.setState({
            pendingChanges,
            changeErrors: undefined,
            lock: pendingChanges ? this.state.lock : UNLOCKED,
        });
    };

    setChangeErrors = (changeErrors: ChangeError[]) => {
        if (!this.mounted) {
            return;
        }
        this.setState({
            pendingChanges: false,
            changeErrors,
        });
    };

    lockValue = (
        value: string,
        validating: boolean | undefined,
        error: ChangeError[] | undefined
    ) => {
        this.setState({
            lock: { type: "Locked", value },
            pendingChanges: validating ?? false,
            changeErrors: error,
        });
    };

    unlockValue = () => {
        if (this.state.lock.type === "Locked") {
            this.setState({
                lock: UNLOCKED,
                pendingChanges: false,
                changeErrors: undefined,
            });
        }
    };

    componentDidMount = () => {
        this.mounted = true;
    };

    componentWillUnmount = () => {
        this.mounted = false;
    };
}

interface NumericInputCommonProps {
    label: string;
}

type NumericInputMetadataFieldProps<ROOT> = T.SharedProperties<
    string,
    ROOT,
    CT.NumericInputInstance
>;

class NumericInputMetadataField<R> extends React.PureComponent<
    NumericInputMetadataFieldProps<R>
> {
    render = () => {
        const { id, label, instance } = this.props;
        const { value } = instance;
        switch (this.props.type) {
            case "READ_ONLY": {
                return (
                    <BaseFormDisplay label={label}>
                        <Text
                            type={value === undefined ? "secondary" : undefined}
                        >
                            {value === undefined
                                ? t("Form_No_Field_Set")
                                : formatNumber(value)}
                        </Text>
                    </BaseFormDisplay>
                );
            }
            case "EDIT": {
                const {
                    type: _type,
                    instance: _instance,
                    ...rest
                } = this.props;
                return (
                    <EditNumericInput
                        {...rest}
                        value={value}
                        onChange={this.handleOnChange}
                    />
                );
            }
            case "CREATE": {
                const {
                    type: _type,
                    instance: _instance,
                    form,
                    ...rest
                } = this.props;
                return (
                    <CreateNumericInput
                        {...rest}
                        form={mapInstanceFormToStringForm(id, form, instance)}
                    />
                );
            }
            default:
                return assertNever(this.props);
        }
    };

    private handleOnChange = (value: number | undefined) => {
        const { instance } = this.props;
        if (this.props.type === "EDIT") {
            return this.props.onChangeAsync(
                Promise.resolve(value),
                (_prev, val) =>
                    U.extractMetadataInstanceValue(
                        numberToInstance(val, instance)
                    )
            );
        }
        return Promise.reject(
            new Error("Cannot change value in non-edit mode")
        );
    };
}

function instanceToString(instance: CT.MetadataInstance): string {
    if (instance.type !== "NumericInputInstance") {
        throw new Error(
            "Expected numeric input instance, got " + instance.type
        );
    }
    return instance.value !== undefined ? instance.value.toString() : "";
}

function stringToInstance(
    numberValue: string,
    oldInstance: CT.MetadataInstance
): CT.MetadataInstance {
    if (oldInstance.type !== "NumericInputInstance") {
        throw new Error("Expected numeric instance, got " + oldInstance.type);
    }
    const value = parseNumber(numberValue);
    return {
        ...oldInstance,
        value,
    };
}

function numberToInstance(
    value: number | undefined,
    oldInstance: CT.MetadataInstance
): CT.MetadataInstance {
    if (oldInstance.type !== "NumericInputInstance") {
        throw new Error("Expected numeric instance, got " + oldInstance.type);
    }
    return {
        ...oldInstance,
        value,
    };
}

function parseNumber(numberValue: string): number | undefined {
    const strValue = numberValue.trim();
    if (strValue !== "") {
        const value = Number(strValue);
        if (isNaN(value)) {
            return undefined;
        } else {
            return value;
        }
    } else {
        return undefined;
    }
}

function mapInstanceFormToStringForm<ROOT>(
    id: string,
    instanceForm: WrappedFormUtils<
        {
            [x: string]: CT.MetadataInstance;
        },
        ROOT
    >,
    instance: CT.MetadataInstance
): WrappedFormUtils<
    {
        [x: string]: string;
    },
    ROOT
> {
    const mapper = simpleMappers<string, CT.MetadataInstance, string, ROOT>(
        id,
        instanceToString,
        (optionIds) => stringToInstance(optionIds, instance)
    );
    return mapForm<Record<string, string>>()(instanceForm, mapper());
}
export type {
    NumericInputMetadataFieldProps,
    NumericInputCommonProps,
    CreateNumericMetadataFieldProps,
};
export { NumericInputMetadataField };
